import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { packOrder } from './game';
import { formatXp } from './format';
import { UT_LOGO } from './primitives';
import { tierGradient, utTier } from './tiers';
import type { UtCardPlayer } from './UtCard';
import { TokenChips } from './TokenChips';

type Stage = 'sealed' | 'burst' | 'reveal';

/** Timings from the handoff README (Interactions → Team pack). */
const BURST_MS = 480;
const FLIP_EVERY_MS = 520;
const FLIP_S = 0.6;
const FACE_SWAP_S = 0.28;

interface Props {
  open: boolean;
  onClose: () => void;
  /** "See both line-ups" (closes and scrolls to the pitch). Defaults to onClose. */
  onSeeLineups?: () => void;
  team: 'blue' | 'orange';
  players: UtCardPlayer[];
  matchNumber: number | string;
}

/**
 * Full-screen "team pack" reveal. Sealed pack → tap → burst → the user's team
 * appears face down and flips one card every 520ms, lowest XP first, best last.
 */
export const UtTeamPack: React.FC<Props> = ({ open, onClose, onSeeLineups, team, players, matchNumber }) => {
  const [stage, setStage] = useState<Stage>('sealed');
  const [revealed, setRevealed] = useState(0);
  const timers = useRef<number[]>([]);
  const ordered = packOrder(players);
  const total = ordered.length;

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  const reset = useCallback(() => {
    clearTimers();
    setStage('sealed');
    setRevealed(0);
  }, []);

  useEffect(() => {
    if (open) reset();
    return clearTimers;
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const tear = () => {
    if (stage !== 'sealed') return;
    setStage('burst');
    timers.current.push(
      window.setTimeout(() => {
        setStage('reveal');
        setRevealed(0);
        for (let i = 1; i <= total; i++) {
          timers.current.push(window.setTimeout(() => setRevealed(i), i * FLIP_EVERY_MS));
        }
      }, BURST_MS),
    );
  };

  if (!open) return null;
  const teamColor = team === 'blue' ? '#2f78d6' : '#ec7a2e';
  const done = stage === 'reveal' && revealed >= total;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Team pack"
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-6 overflow-auto p-6 font-ut-body text-[#f2efe6]"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, #2a2210 0%, #07090f 65%)' }}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-5 top-[18px] rounded-lg border border-white/20 px-3 py-2 font-ut-display text-sm font-bold tracking-[0.12em]"
      >
        CLOSE
      </button>

      {stage !== 'reveal' && (
        <div className="flex flex-col items-center gap-[18px]">
          <motion.button
            type="button"
            onClick={tear}
            aria-label="Open the pack"
            className="ut-shield-lg flex w-[230px] flex-col items-center justify-center gap-2.5 text-[#1a1204]"
            style={{
              aspectRatio: '0.7',
              clipPath: 'polygon(0 6%, 50% 0, 100% 6%, 100% 92%, 50% 100%, 0 92%)',
              background: 'linear-gradient(150deg, #fff1b8, #e8c15a 30%, #9c7420 60%, #f7de8e 85%)',
            }}
            animate={stage === 'burst' ? { scale: 1.35, rotate: -4, opacity: 0 } : { scale: 1, rotate: 0, opacity: 1 }}
            transition={{ duration: 0.45, ease: [0.3, 1.4, 0.5, 1] }}
          >
            <img src={UT_LOGO} alt="" className="w-[110px]" />
            <span className="font-ut-display text-2xl font-extrabold tracking-[0.1em]">TEAM PACK</span>
            <span className="font-ut-display text-sm font-bold tracking-[0.16em]">MATCH #{matchNumber}</span>
          </motion.button>
          <span className="font-ut-display text-base font-bold tracking-[0.2em] text-[#e8c15a]">TAP TO OPEN</span>
        </div>
      )}

      {stage === 'reveal' && (
        <div className="flex w-full max-w-[560px] flex-col items-center gap-[18px]">
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-ut-display text-[13px] font-bold tracking-[0.2em] text-[#e8c15a]">MATCH #{matchNumber} · YOUR TEAM</span>
            <span className="font-ut-display text-[44px] font-extrabold uppercase leading-none" style={{ color: teamColor }}>
              {team}
            </span>
            <span className="text-[13px] text-[#8f97aa]" aria-live="polite">
              {Math.min(revealed, total)} / {total} revealed · best card last
            </span>
          </div>
          <div className="grid w-full grid-cols-[repeat(3,minmax(0,110px))] justify-center gap-3">
            {ordered.map((p, i) => (
              <PackCard key={p.id} player={p} faceUp={revealed > i} />
            ))}
          </div>
          {done && (
            <div className="flex flex-wrap justify-center gap-2.5">
              <button
                type="button"
                onClick={reset}
                className="rounded-xl border border-white/20 px-[18px] py-3 font-ut-display text-[17px] font-extrabold tracking-[0.06em]"
              >
                REPLAY
              </button>
              <button
                type="button"
                onClick={onSeeLineups ?? onClose}
                className="rounded-xl bg-[#e8c15a] px-[18px] py-3 font-ut-display text-[17px] font-extrabold tracking-[0.06em] text-[#1a1204]"
              >
                SEE BOTH LINE-UPS
              </button>
            </div>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
};

const PackCard: React.FC<{ player: UtCardPlayer; faceUp: boolean }> = ({ player, faceUp }) => {
  const tier = utTier(player.rarity, player.xp);
  const face: React.CSSProperties = { backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' };
  return (
    <div style={{ aspectRatio: '0.72', perspective: 900 }}>
      <motion.div
        className="relative h-full w-full"
        style={{ transformStyle: 'preserve-3d' }}
        initial={{ rotateY: 180 }}
        animate={{ rotateY: faceUp ? 0 : 180 }}
        transition={{ duration: FLIP_S, ease: [0.3, 0.7, 0.2, 1] }}
      >
        <motion.div
          className="ut-shield absolute inset-0 box-border flex flex-col items-center justify-between"
          style={{ ...face, background: tierGradient(tier), color: tier.ink, padding: '12px 6px 14px' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: faceUp ? 1 : 0 }}
          transition={{ duration: 0, delay: FACE_SWAP_S }}
        >
          <TokenChips playerId={player.id} size={18} className="absolute right-[9%] top-[10%] z-[2]" />
          <span className="flex flex-col self-start pl-1 font-ut-display leading-none">
            <span className="text-[22px] font-extrabold">{formatXp(player.xp)}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.1em]">
              XP{player.position ? ` · ${player.position}` : ''}
              {player.whatsappMember && <FaWhatsapp size={11} className="opacity-50" aria-label="WhatsApp group member" />}
            </span>
          </span>
          <span className="text-center font-ut-display text-sm font-bold uppercase leading-[1.05]">{player.name}</span>
          <span className="font-ut-display text-[9px] font-bold uppercase tracking-[0.14em] opacity-85">{tier.label}</span>
        </motion.div>
        <motion.div
          className="ut-shield absolute inset-0 isolate flex items-center justify-center border border-white/10"
          style={{ ...face, transform: 'rotateY(180deg)', background: 'linear-gradient(160deg, #1f2536, #0b0f18)' }}
          initial={{ opacity: 1 }}
          animate={{ opacity: faceUp ? 0 : 1 }}
          transition={{ duration: 0, delay: FACE_SWAP_S }}
        >
          <img
            src={UT_LOGO}
            alt=""
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 -z-10 w-4/5 -translate-x-1/2 -translate-y-1/2 opacity-[0.14] grayscale"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

const SEEN_PREFIX = 'ut-pack-seen:';

/** Per-game "pack already shown" flag in localStorage. Storage can throw (private mode), so every access is guarded. */
export function hasSeenPack(gameId: string): boolean {
  try {
    return window.localStorage.getItem(SEEN_PREFIX + gameId) === '1';
  } catch {
    return true; // can't remember it, so don't force it on every visit
  }
}

export function markPackSeen(gameId: string): void {
  try {
    window.localStorage.setItem(SEEN_PREFIX + gameId, '1');
  } catch {
    /* ignore */
  }
}
